import React, { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Calendar, Send, AlertCircle, ArrowRight, Check, Sparkles } from "lucide-react";
import { Language } from "../../types";
import { swissReveal, goldenSpring } from "../../lib/animations";

interface Props {
	lang: Language;
}

// Confetti particle component
const ConfettiParticle: React.FC<{ delay: number; x: number; color: string }> = ({ delay, x, color }) => (
	<motion.div
		className="absolute w-2 h-2 rounded-full"
		style={{ backgroundColor: color, left: `${x}%` }}
		initial={{ y: 0, opacity: 1, scale: 1 }}
		animate={{
			y: [-20, -80, 20],
			opacity: [1, 1, 0],
			scale: [1, 1.2, 0.8],
			x: [0, (Math.random() - 0.5) * 60],
			rotate: [0, 360],
		}}
		transition={{
			duration: 1.5,
			delay,
			ease: "easeOut",
		}}
	/>
);

// Success animation component
const SuccessAnimation: React.FC<{ message: string }> = ({ message }) => {
	const particles = [
		{ delay: 0, x: 30, color: '#0d9488' },
		{ delay: 0.1, x: 45, color: '#2dd4bf' },
		{ delay: 0.05, x: 55, color: '#14b8a6' },
		{ delay: 0.15, x: 70, color: '#0d9488' },
		{ delay: 0.08, x: 40, color: '#5eead4' },
		{ delay: 0.12, x: 60, color: '#2dd4bf' },
	];

	return (
		<motion.div
			initial={{ opacity: 0 }}
			animate={{ opacity: 1 }}
			className="relative py-16 text-center"
		>
			{/* Confetti particles */}
			<div className="absolute inset-0 overflow-hidden pointer-events-none">
				{particles.map((p, i) => (
					<ConfettiParticle key={i} {...p} />
				))}
			</div>

			{/* Success checkmark */}
			<motion.div
				initial={{ scale: 0, rotate: -180 }}
				animate={{ scale: 1, rotate: 0 }}
				transition={{
					type: "spring",
					stiffness: 200,
					damping: 15,
					delay: 0.1,
				}}
				className="relative w-20 h-20 bg-gradient-to-br from-primary to-teal-400 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg shadow-primary/30"
			>
				<motion.div
					initial={{ pathLength: 0, opacity: 0 }}
					animate={{ pathLength: 1, opacity: 1 }}
					transition={{ delay: 0.4, duration: 0.4 }}
				>
					<Check className="w-10 h-10 text-white" strokeWidth={3} />
				</motion.div>

				{/* Ripple effect */}
				<motion.div
					className="absolute inset-0 rounded-full border-2 border-primary"
					initial={{ scale: 1, opacity: 0.5 }}
					animate={{ scale: 1.8, opacity: 0 }}
					transition={{ duration: 0.8, delay: 0.3 }}
				/>
				<motion.div
					className="absolute inset-0 rounded-full border-2 border-primary"
					initial={{ scale: 1, opacity: 0.3 }}
					animate={{ scale: 2.2, opacity: 0 }}
					transition={{ duration: 1, delay: 0.5 }}
				/>
			</motion.div>

			{/* Success message */}
			<motion.div
				initial={{ opacity: 0, y: 10 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ delay: 0.5 }}
			>
				<motion.div
					initial={{ opacity: 0, scale: 0.8 }}
					animate={{ opacity: 1, scale: 1 }}
					transition={{ delay: 0.6 }}
					className="flex items-center justify-center gap-2 mb-3"
				>
					<Sparkles className="w-5 h-5 text-primary" />
					<span className="text-sm font-medium text-primary uppercase tracking-wider">Message Sent</span>
					<Sparkles className="w-5 h-5 text-primary" />
				</motion.div>
				<p className="text-xl md:text-2xl font-semibold text-brandDark">
					{message}
				</p>
			</motion.div>
		</motion.div>
	);
};

const ContactSection: React.FC<Props> = ({ lang }) => {
	const [name, setName] = useState("");
	const [email, setEmail] = useState("");
	const [message, setMessage] = useState("");
	const [company, setCompany] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isSubmitted, setIsSubmitted] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const firstInteractionRef = useRef<number | null>(null);

	const minSubmitMs = 800;

	const content = {
		label: {
			en: "GET IN TOUCH",
			fa: "تماس با ما",
		},
		title: {
			en: "Let's talk about your journey",
			fa: "بیا درباره سفرت صحبت کنیم",
		},
		description: {
			en: "A free, honest conversation. No pressure, no commitment — just clarity.",
			fa: "یک گپ صادقانه و رایگان. بدون فشار، بدون تعهد — فقط شفافیت.",
		},
		buttonText: {
			en: "Book Discovery Session",
			fa: "رزرو جلسه آشنایی",
		},
		formTitle: {
			en: "Send a message",
			fa: "پیام بفرست",
		},
		namePlaceholder: {
			en: "Your name",
			fa: "نام شما",
		},
		emailPlaceholder: {
			en: "your@email.com",
			fa: "ایمیل شما",
		},
		messagePlaceholder: {
			en: "What's on your mind?",
			fa: "چه چیزی در ذهنته؟",
		},
		submitButton: {
			en: "Send Message",
			fa: "ارسال پیام",
		},
		successMessage: {
			en: "Thanks! I'll be in touch soon.",
			fa: "ممنون! به زودی باهات تماس میگیرم.",
		},
		errorMessage: {
			en: "Something went wrong. Please try again.",
			fa: "مشکلی پیش آمد. لطفا دوباره تلاش کنید.",
		},
	};

	const markInteracted = () => {
		if (firstInteractionRef.current === null) {
			firstInteractionRef.current = Date.now();
		}
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (isSubmitting) return;
		if (!email || !name || !message) return;

		// Honeypot check - if company field is filled, it's a bot
		if (company) {
			setIsSubmitted(true);
			return;
		}

		// Time-based spam check
		if (firstInteractionRef.current !== null) {
			const elapsed = Date.now() - firstInteractionRef.current;
			if (elapsed < minSubmitMs) return;
		}

		setIsSubmitting(true);
		setError(null);

		try {
			const formData = new FormData();
			formData.append("access_key", "77a43823-2b67-44d5-bc4a-a490b37909dd");
			formData.append("name", name);
			formData.append("email", email);
			formData.append("message", message);
			formData.append("subject", `New message from ${name}`);

			const response = await fetch("https://api.web3forms.com/submit", {
				method: "POST",
				body: formData,
			});

			const data = await response.json();

			if (!data.success) {
				throw new Error("Failed to send");
			}

			setIsSubmitted(true);
			setName("");
			setEmail("");
			setMessage("");
			setCompany("");
		} catch {
			setError(content.errorMessage[lang]);
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<section className="relative py-28 md:py-36 bg-gradient-to-b from-white to-stone-50 overflow-hidden">
			{/* Background decorative shapes */}
			<div className="absolute inset-0 pointer-events-none overflow-hidden">
				{/* Top right blob */}
				<motion.div
					initial={{ opacity: 0, scale: 0.8 }}
					whileInView={{ opacity: 1, scale: 1 }}
					viewport={{ once: true }}
					transition={{ duration: 1.5, ease: "easeOut" }}
					className="absolute -top-32 -right-32 w-96 h-96 bg-gradient-to-br from-primary/10 via-teal-300/10 to-emerald-200/5 rounded-full blur-3xl"
				/>
				{/* Bottom left blob */}
				<motion.div
					initial={{ opacity: 0, scale: 0.8 }}
					whileInView={{ opacity: 1, scale: 1 }}
					viewport={{ once: true }}
					transition={{ duration: 1.5, delay: 0.2, ease: "easeOut" }}
					className="absolute -bottom-48 -left-48 w-[500px] h-[500px] bg-gradient-to-tr from-teal-200/15 via-primary/10 to-transparent rounded-full blur-3xl"
				/>
				{/* Center accent */}
				<motion.div
					initial={{ opacity: 0 }}
					whileInView={{ opacity: 1 }}
					viewport={{ once: true }}
					transition={{ duration: 2, delay: 0.4 }}
					className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-gradient-to-r from-transparent via-primary/5 to-transparent rounded-full blur-3xl"
				/>
				{/* Floating circles */}
				<motion.div
					animate={{ y: [-10, 10, -10] }}
					transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
					className="absolute top-1/4 right-1/4 w-4 h-4 bg-primary/20 rounded-full"
				/>
				<motion.div
					animate={{ y: [10, -10, 10] }}
					transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
					className="absolute bottom-1/3 left-1/5 w-6 h-6 bg-teal-300/20 rounded-full"
				/>
				<motion.div
					animate={{ y: [-5, 15, -5] }}
					transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
					className="absolute top-1/3 left-1/4 w-3 h-3 bg-emerald-300/30 rounded-full"
				/>
			</div>

			<div className="relative px-6 lg:px-12 mx-auto max-w-7xl">
				{/* Centered Header */}
				<motion.div
					initial={{ opacity: 0, y: 20 }}
					whileInView={{ opacity: 1, y: 0 }}
					viewport={{ once: true, margin: "-100px" }}
					transition={swissReveal}
					className="text-center max-w-3xl mx-auto mb-16"
				>
					<span className="swiss-label text-primary mb-4 block">
						{content.label[lang]}
					</span>
					<h2 className="swiss-headline text-4xl md:text-5xl lg:text-6xl text-brandDark mb-6">
						{content.title[lang]}
					</h2>
					<p className="swiss-body text-lg md:text-xl text-gray-500">
						{content.description[lang]}
					</p>
				</motion.div>

				{/* Two-column layout: Equal height cards */}
				<div className="grid lg:grid-cols-2 gap-8 lg:gap-10 items-stretch">
					{/* Left: Primary CTA Card */}
					<motion.div
						initial={{ opacity: 0, y: 30, scale: 0.98 }}
						whileInView={{ opacity: 1, y: 0, scale: 1 }}
						viewport={{ once: true, margin: "-100px" }}
						transition={{ ...goldenSpring, delay: 0.1 }}
						className="h-full"
					>
						<div className="relative bg-brandDark rounded-3xl p-6 md:p-10 lg:p-12 text-white overflow-hidden h-full flex flex-col">
							{/* Decorative gradient orbs */}
							<div className="absolute -top-20 -right-20 w-48 h-48 bg-primary/40 rounded-full blur-3xl" />
							<div className="absolute top-1/2 -left-16 w-40 h-40 bg-teal-400/30 rounded-full blur-3xl" />
							<div className="absolute -bottom-10 right-1/4 w-32 h-32 bg-emerald-400/20 rounded-full blur-2xl" />

							{/* Subtle grid pattern */}
							<div
								className="absolute inset-0 opacity-[0.03]"
								style={{
									backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
									backgroundSize: '40px 40px',
								}}
							/>

							<div className="relative flex-1 flex flex-col">
								<motion.div
									initial={{ opacity: 0, x: -20 }}
									whileInView={{ opacity: 1, x: 0 }}
									viewport={{ once: true }}
									transition={{ delay: 0.3 }}
									className="mb-6"
								>
									<span className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/10 rounded-full text-sm text-white/80 backdrop-blur-sm">
										<Calendar className="w-4 h-4" />
										{lang === 'en' ? 'Free 30-min call' : 'تماس رایگان ۳۰ دقیقه‌ای'}
									</span>
								</motion.div>

								<h3 className="swiss-subhead text-2xl md:text-3xl text-white mb-4">
									{lang === 'en' ? 'Book a free call' : 'رزرو تماس رایگان'}
								</h3>
								<p className="text-white/70 mb-8 leading-relaxed text-lg flex-1">
									{lang === 'en'
										? "30 minutes to explore what's possible. No sales pitch, just an honest conversation about where you are and where you want to be."
										: "۳۰ دقیقه برای کشف امکانات. بدون فروش، فقط یک گفتگوی صادقانه درباره جایی که هستی و جایی که می‌خوای باشی."
									}
								</p>

								<motion.a
									href="https://calendar.app.google/NbuyxdkKnzVU9gj17"
									target="_blank"
									rel="noopener noreferrer"
									whileHover={{ scale: 1.02, y: -2 }}
									whileTap={{ scale: 0.98 }}
									className="group inline-flex items-center justify-center gap-3 bg-white text-brandDark px-6 py-4 md:px-8 md:py-5 min-h-[52px] rounded-2xl font-semibold text-base md:text-lg shadow-lg shadow-black/20 hover:shadow-xl hover:shadow-black/30 transition-shadow active:scale-[0.98]"
								>
									<Calendar className="w-5 h-5 text-primary" aria-hidden="true" />
									{content.buttonText[lang]}
									<ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform text-primary" aria-hidden="true" />
								</motion.a>
							</div>
						</div>
					</motion.div>

					{/* Right: Contact Form */}
					<motion.div
						initial={{ opacity: 0, y: 30, scale: 0.98 }}
						whileInView={{ opacity: 1, y: 0, scale: 1 }}
						viewport={{ once: true, margin: "-100px" }}
						transition={{ ...goldenSpring, delay: 0.2 }}
						className="h-full"
					>
						<div className="bg-white rounded-3xl p-6 md:p-10 lg:p-12 shadow-2xl shadow-black/[0.08] border border-gray-100/80 h-full flex flex-col">
							<AnimatePresence mode="wait">
								{isSubmitted ? (
									<motion.div
										key="success"
										initial={{ opacity: 0 }}
										animate={{ opacity: 1 }}
										exit={{ opacity: 0 }}
										className="flex-1 flex items-center justify-center"
									>
										<SuccessAnimation message={content.successMessage[lang]} />
									</motion.div>
								) : error ? (
									<motion.div
										key="error"
										role="alert"
										aria-live="assertive"
										initial={{ opacity: 0, scale: 0.95 }}
										animate={{ opacity: 1, scale: 1 }}
										exit={{ opacity: 0 }}
										className="flex-1 flex flex-col items-center justify-center py-8"
									>
										<motion.div
											initial={{ scale: 0 }}
											animate={{ scale: 1 }}
											transition={{ type: "spring", stiffness: 300 }}
											className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4"
										>
											<AlertCircle className="w-8 h-8 text-red-500" aria-hidden="true" />
										</motion.div>
										<p className="font-medium text-red-600 mb-4 text-center">{error}</p>
										<motion.button
											whileHover={{ scale: 1.05 }}
											whileTap={{ scale: 0.95 }}
											onClick={() => setError(null)}
											className="px-6 py-2.5 bg-gray-100 hover:bg-gray-200 rounded-xl text-sm font-medium text-gray-700 transition-colors"
										>
											{lang === "en" ? "Try Again" : "تلاش مجدد"}
										</motion.button>
									</motion.div>
								) : (
									<motion.div
										key="form"
										initial={{ opacity: 1 }}
										exit={{ opacity: 0, y: -20 }}
										className="flex-1 flex flex-col"
									>
										<motion.h3
											initial={{ opacity: 0, y: -10 }}
											animate={{ opacity: 1, y: 0 }}
											className="swiss-subhead text-2xl md:text-3xl text-brandDark mb-8"
										>
											{content.formTitle[lang]}
										</motion.h3>

										<form
											onSubmit={handleSubmit}
											onFocusCapture={markInteracted}
											className="space-y-5 flex-1 flex flex-col"
										>
											{/* Honeypot */}
											<input
												type="text"
												name="company"
												value={company}
												onChange={(e) => setCompany(e.target.value)}
												tabIndex={-1}
												autoComplete="off"
												aria-hidden="true"
												style={{
													position: "absolute",
													left: "-10000px",
													top: "auto",
													width: "1px",
													height: "1px",
													overflow: "hidden",
												}}
											/>

											{/* Name & Email Row */}
											<div className="grid sm:grid-cols-2 gap-5">
												<motion.div
													initial={{ opacity: 0, x: -10 }}
													animate={{ opacity: 1, x: 0 }}
													transition={{ delay: 0.1 }}
												>
													<label htmlFor="contact-name" className="swiss-label block text-sm font-medium text-gray-600 mb-2">
														{content.namePlaceholder[lang]}
													</label>
													<input
														id="contact-name"
														type="text"
														name="name"
														value={name}
														onChange={(e) => {
															setName(e.target.value);
															markInteracted();
														}}
														placeholder={lang === 'en' ? 'John Doe' : 'نام کامل'}
														required
														autoComplete="name"
														className="w-full px-5 py-4 min-h-[52px] text-base bg-gray-50/80 border border-gray-200 rounded-xl hover:border-gray-300 hover:bg-gray-50/50 focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:outline-none focus-visible:bg-white transition-all duration-200 text-brandDark placeholder:text-gray-400"
														dir={lang === "fa" ? "rtl" : "ltr"}
													/>
												</motion.div>
												<motion.div
													initial={{ opacity: 0, x: 10 }}
													animate={{ opacity: 1, x: 0 }}
													transition={{ delay: 0.15 }}
												>
													<label htmlFor="contact-email" className="swiss-label block text-sm font-medium text-gray-600 mb-2">
														{content.emailPlaceholder[lang]}
													</label>
													<input
														id="contact-email"
														type="email"
														name="email"
														value={email}
														onChange={(e) => {
															setEmail(e.target.value);
															markInteracted();
														}}
														placeholder={lang === 'en' ? 'you@example.com' : 'example@email.com'}
														required
														autoComplete="email"
														className="w-full px-5 py-4 min-h-[52px] text-base bg-gray-50/80 border border-gray-200 rounded-xl hover:border-gray-300 hover:bg-gray-50/50 focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:outline-none focus-visible:bg-white transition-all duration-200 text-brandDark placeholder:text-gray-400"
														dir={lang === "fa" ? "rtl" : "ltr"}
													/>
												</motion.div>
											</div>

											{/* Message */}
											<motion.div
												initial={{ opacity: 0, y: 10 }}
												animate={{ opacity: 1, y: 0 }}
												transition={{ delay: 0.2 }}
												className="flex-1 flex flex-col"
											>
												<label htmlFor="contact-message" className="swiss-label block text-sm font-medium text-gray-600 mb-2">
													{content.messagePlaceholder[lang]}
												</label>
												<textarea
													id="contact-message"
													name="message"
													value={message}
													onChange={(e) => {
														setMessage(e.target.value);
														markInteracted();
													}}
													placeholder={lang === 'en' ? 'Tell me about what you\'re going through...' : 'درباره چیزی که در ذهنت هست بنویس...'}
													required
													rows={5}
													className="w-full flex-1 min-h-[140px] px-5 py-4 text-base bg-gray-50/80 border border-gray-200 rounded-xl hover:border-gray-300 hover:bg-gray-50/50 focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:outline-none focus-visible:bg-white transition-all duration-200 resize-none text-brandDark placeholder:text-gray-400"
													dir={lang === "fa" ? "rtl" : "ltr"}
												/>
											</motion.div>

											<motion.button
												type="submit"
												disabled={isSubmitting}
												whileHover={{ scale: isSubmitting ? 1 : 1.01, y: isSubmitting ? 0 : -2 }}
												whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
												initial={{ opacity: 0, y: 10 }}
												animate={{ opacity: 1, y: 0 }}
												transition={{ delay: 0.25 }}
												className="w-full flex items-center justify-center gap-3 bg-gradient-to-r from-primary to-teal-500 text-white px-6 py-4 md:px-8 md:py-5 min-h-[52px] rounded-2xl font-semibold text-base md:text-lg shadow-lg shadow-primary/30 hover:shadow-xl hover:shadow-primary/40 disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:shadow-lg transition-shadow active:scale-[0.98]"
											>
												{isSubmitting ? (
													<motion.div
														aria-hidden="true"
														animate={{ rotate: 360 }}
														transition={{
															duration: 1,
															repeat: Infinity,
															ease: "linear",
														}}
														className="w-6 h-6 rounded-full border-2 border-white/30 border-t-white"
													/>
												) : (
													<>
														<Send aria-hidden="true" className="w-5 h-5" />
														{content.submitButton[lang]}
													</>
												)}
											</motion.button>
										</form>
									</motion.div>
								)}
							</AnimatePresence>
						</div>
					</motion.div>
				</div>
			</div>
		</section>
	);
};

export default ContactSection;
